import { useEffect, useRef } from "react";

import { useAsyncTaskStream } from "../../../hooks/useAsyncTaskStream";

type EmbeddingTaskWatcherProps = {
	taskId: string;
	documentId: string;
	onUpdate: (documentId: string, status: string, progressPercent: number, error?: string) => void;
};

/**
 * Invisible component that watches a single embedding task via SSE.
 * Reports task updates (status and progress) via onUpdate callback.
 * This allows multiple tasks to be tracked simultaneously by rendering
 * multiple instances of this component.
 */
export const EmbeddingTaskWatcher = ({ taskId, documentId, onUpdate }: EmbeddingTaskWatcherProps): null => {
	const { task } = useAsyncTaskStream(taskId);
	const hasCompletedRef = useRef(false);
	const lastProgressRef = useRef<number>(-1);

	useEffect(() => {
		if (!task || hasCompletedRef.current) return;

		const progressPercent =
			task.progressTotal > 0 ? Math.round((task.progressCount / task.progressTotal) * 100) : 0;
		const isComplete = task.status === "completed" || task.status === "failed";

		// Only call onUpdate if progress changed or task completed
		if (progressPercent !== lastProgressRef.current || isComplete) {
			lastProgressRef.current = progressPercent;

			if (isComplete) {
				hasCompletedRef.current = true;
			}

			onUpdate(documentId, task.status, progressPercent, task.error);
		}
	}, [task, documentId, onUpdate]);

	return null;
};
